// src/hooks/useEmailPasswordLogin.js
// -----------------------------------------------------------------------------
// Shared email/password login primitive.
//
// Purpose:
// - Centralize Firebase email/password login behavior.
// - Keep modal login and page login from drifting over time.
// - Match the existing Login.jsx safety pattern:
//   signInWithEmailAndPassword can resolve before AuthContext finishes
//   hydrating currentUser/profile/org/permissions.
// - Avoid navigation and UI assumptions.
// - Avoid global loading overlays unless a caller explicitly adds them.
// -----------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
  signInWithEmailAndPassword,
} from 'firebase/auth';

import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

const DEFAULT_ERROR_MESSAGE = 'Incorrect email or password.';

export default function useEmailPasswordLogin({
  onSuccess,
  onError,
  errorMessage = DEFAULT_ERROR_MESSAGE,
} = {}) {
  const { currentUser, authLoading, initialized } = useAuth();

  const isMountedRef = useRef(true);
  const successHandledRef = useRef(false);

  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingSuccess, setPendingSuccess] = useState(false);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Match Login.jsx's enterprise-safe login behavior:
  // do not treat signInWithEmailAndPassword as the final ready signal.
  // Wait until AuthContext exposes currentUser.
  useEffect(() => {
    if (!pendingSuccess) return;
    if (!currentUser) return;
    if (successHandledRef.current) return;

    successHandledRef.current = true;

    if (isMountedRef.current) {
      setIsSubmitting(false);
      setPendingSuccess(false);
    }

    if (typeof onSuccess === 'function') {
      onSuccess(currentUser);
    }
  }, [currentUser, onSuccess, pendingSuccess]);

  const reset = useCallback(() => {
    successHandledRef.current = false;

    if (!isMountedRef.current) return;

    setError('');
    setIsSubmitting(false);
    setPendingSuccess(false);
  }, []);

  const login = useCallback(
    async ({
      email,
      password,
      rememberMe = false,
    }) => {
      if (isSubmitting || pendingSuccess) {
        console.warn('[useEmailPasswordLogin] Login already in progress.');
        return;
      }

      const normalizedEmail = String(email || '').trim();

      if (!normalizedEmail || !password) {
        if (isMountedRef.current) {
          setError('Email and password are required.');
        }
        return;
      }

      console.log(
        '[useEmailPasswordLogin] Attempting email/password login.',
        { rememberMe },
      );

      successHandledRef.current = false;

      if (isMountedRef.current) {
        setError('');
        setIsSubmitting(true);
        setPendingSuccess(false);
      }

      try {
        const persistenceType = rememberMe
          ? browserLocalPersistence
          : browserSessionPersistence;

        console.log(
          '[useEmailPasswordLogin] Setting persistence:',
          rememberMe ? 'Local' : 'Session',
        );

        await setPersistence(auth, persistenceType);
        console.log('[useEmailPasswordLogin] Persistence set successfully.');

        await signInWithEmailAndPassword(
          auth,
          normalizedEmail,
          password,
        );

        console.log('[useEmailPasswordLogin] Firebase sign-in successful.');

        if (isMountedRef.current) {
          setPendingSuccess(true);
        }
      } catch (loginError) {
        console.error(
          '[useEmailPasswordLogin] Error signing in:',
          loginError?.message || loginError,
        );

        if (isMountedRef.current) {
          setError(errorMessage);
          setIsSubmitting(false);
          setPendingSuccess(false);
        }

        if (typeof onError === 'function') {
          onError(loginError);
        }
      }
    },
    [
      errorMessage,
      isSubmitting,
      onError,
      pendingSuccess,
    ],
  );

  const isResolvingAuth =
    pendingSuccess ||
    (
      isSubmitting &&
      (authLoading || !initialized)
    );

  return {
    error,
    isSubmitting,
    pendingSuccess,
    isResolvingAuth,
    isBusy: isSubmitting || pendingSuccess,
    login,
    reset,
  };
}